import z from "zod/v4";
import { CustomerType } from "../customer/index.js";
import { OptionalEmailSchema } from "../customer/schemas/email-schema.js";
import { CART_META_KEY, CartType, DEFAULT_CART_EXPIRATION_IN_SECONDS, DEFAULT_CART_STALE_TTL_IN_SECONDS, } from "./constants.js";
export const customerAddressSchema = z.object({
    type: z.enum(["billing", "delivery", "other"]),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    city: z.string(),
    email: OptionalEmailSchema,
    phone: z.string().nullish(),
    country: z.string(),
    street: z.string(),
    postalCode: z.string(),
    meta: z
        .array(z.object({
        key: z.string(),
        value: z.string(),
    }))
        .optional(),
});
export const cartCustomerInputSchema = z.object({
    type: z.enum(CustomerType).optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    addresses: z.array(customerAddressSchema).optional(),
    phone: z.string().nullish(),
    email: OptionalEmailSchema,
    birthDate: z.string().nullish(),
    identifier: z.string().optional(),
    isGuest: z.boolean(),
    meta: z
        .array(z.object({
        key: z.string(),
        value: z.string(),
    }))
        .optional(),
    companyName: z.string().optional(),
});
export const cartMetaInputSchema = z.object({
    meta: z.array(z.object({
        key: z.string(),
        value: z.string(),
    })),
    merge: z.boolean().optional(),
});
export const cartCustomerSchema = z.object({
    type: z.enum(CustomerType).nullish(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    addresses: z.array(customerAddressSchema).nullish(),
    phone: z.string().nullish(),
    email: OptionalEmailSchema,
    birthDate: z.string().nullish(),
    identifier: z.string().nullish(),
    isGuest: z.boolean().nullish(),
});
export const addSkuItemInput = z.object({
    sku: z.string(),
    quantity: z.number().gt(0).optional(),
    taxRate: z.number().gt(0).lt(1).optional(),
});
export const cartItemPricingChangeInput = z.object({
    sku: z.string(),
    quantity: z.number(),
    price: z.object({ gross: z.number(), net: z.number() }),
});
export const cartContextInputPrice = z.object({
    decimals: z.number().default(2).optional(),
    pricesHaveTaxesIncludedInCrystallize: z.boolean().default(false).optional(),
    selectedVariantIdentifier: z.string().default("default").optional(),
    compareAtVariantIdentifier: z.string().default("default").optional(),
    discountOnNetPrices: z.boolean().default(false).optional(),
    markets: z.array(z.any()).default([]).optional(),
    customerGroups: z.array(z.any()).default([]).optional(),
    currency: z.enum(["NOK"]).default("NOK").optional(),
    voucherCodes: z.array(z.string()).optional(),
});
export const cartContextInput = z
    .object({
    price: cartContextInputPrice.default({}).optional(),
    language: z.enum(["en", "no-nb"]).default("no-nb").optional(),
    ttl: z.number().default(DEFAULT_CART_EXPIRATION_IN_SECONDS).optional(),
    staleTtl: z.number().default(DEFAULT_CART_STALE_TTL_IN_SECONDS).optional(),
})
    .default({
    price: cartContextInputPrice.parse({}),
    language: "no-nb",
    ttl: DEFAULT_CART_EXPIRATION_IN_SECONDS,
    staleTtl: DEFAULT_CART_STALE_TTL_IN_SECONDS,
})
    .optional();
export const hydrateCartInput = z.object({
    id: z.uuid().optional(),
    items: z.array(addSkuItemInput).optional(),
    customer: cartCustomerInputSchema.optional(),
    context: cartContextInput.optional(),
    meta: z
        .array(z.object({
        key: z.enum(CART_META_KEY),
        value: z.string(),
    }))
        .optional(),
});
export const cartResultPrice = z.object({
    gross: z.number(),
    net: z.number(),
    taxAmount: z.number(),
    currency: z.string(),
    discounts: z
        .array(z.object({
        percent: z.number(),
        amount: z.number(),
    }))
        .optional(),
});
export const cartResultItem = z.object({
    name: z.string(),
    quantity: z.number().gt(0),
    variant: z.object({
        sku: z.string(),
        attributes: z
            .partialRecord(z
            .enum(["Innbinding", "Språk", "Forfattervisning", "frakt type"])
            .or(z.string()), z.string().optional())
            .optional(),
        product: z.object({ id: z.string(), path: z.string() }),
        price: cartResultPrice,
        compareAtPrice: cartResultPrice.nullable(),
        name: z.string().optional(),
    }),
    images: z
        .array(z.object({
        url: z.url(),
    }))
        .optional(),
    price: cartResultPrice,
});
export const cartResult = z.object({
    id: z.string(),
    isStale: z.boolean().nullish(),
    staleAt: z.iso.datetime().nullish(),
    items: z.array(cartResultItem),
    state: z.enum(["cart", "placed", "ordered", "abandoned"]),
    orderId: z.string().nullish(),
    updatedAt: z.iso.datetime(),
    total: cartResultPrice,
    customer: cartCustomerSchema.nullish(),
    cartApiVersion: z.string().nullish(),
    cartType: z
        .enum(CartType)
        .nullable()
        .transform((value) => value ?? CartType.SHOP), // Default to SHOP, needed for existing carts without cartType meta. Can be removed 80 days after merge.
    context: cartContextInput.optional(),
    freeShippingThreshold: z.string().nullish(),
});
